/**
 * Browser-Merker des Leitfadens (docs/planungsphase-leitfaden-plan.md, 2.2
 * und Entscheide E-L1, E-L7).
 *
 * Hier liegt NUR Bequemlichkeit: zuletzt besuchte Station je Projekt, die
 * gewaehlte Ansicht und weggeklickte Hilfekaesten. Der Stand der Stationen
 * selbst kommt immer aus den Daten (`leitfadenStand`) — faellt der Speicher
 * aus, landet man an der berechneten Station und sieht die Hilfen noch
 * einmal, mehr nicht. „Rest bleibt offen" (Station 3) steht NICHT hier,
 * sondern am Kopfsatz (`ProjektMerkmale.leitfadenRestOffen`): sonst kennten
 * Dashboard und Aufgabenliste ihn nicht. Ein `restOffen` aus aelteren
 * Staenden dieses Merkers wird beim Lesen uebergangen und beim naechsten
 * Schreiben nicht mehr mitgeschrieben.
 *
 * `localStorage` kann fehlen oder werfen (privates Fenster, App-WebView,
 * Quota). Deshalb steht JEDER Zugriff in try/catch, und ein Fehler wird
 * geschluckt: Rueckfall = nichts gemerkt. Unlesbares JSON oder eine fremde
 * Form gilt als leer. Speicher injiziert (`SpeicherLike`), damit die Tests
 * ohne DOM laufen — Vorbild `src/services/navigationPersistence.ts`.
 *
 * Ein JSON-Objekt unter EINEM Schluessel:
 *   { ansicht?, hilfe?: string[], projekte?: { [projectId]: { station, am } } }
 */

import type { LeitfadenMerker, PlanungAnsichtArt, StationSchluessel } from './leitfadenTypen';
import { istStationSchluessel } from './leitfaden';

export interface SpeicherLike {
    getItem(k: string): string | null;
    setItem(k: string, v: string): void;
}

export const LEITFADEN_SPEICHER_SCHLUESSEL = 'osia_planung_leitfaden_v1';

/** So viele Projekte behaelt der Merker; die aeltesten (nach `am`) fallen
 *  heraus. Ein Koordinator hat selten mehr als ein paar Dutzend Phasen offen. */
const MAX_PROJEKTE = 200;

/** `window.localStorage`, oder `null`, wo es keins gibt oder schon der
 *  Zugriff wirft (gesperrte Website-Daten). */
export const browserSpeicher = (): SpeicherLike | null => {
    try {
        return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
    } catch {
        return null;
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// ABLAGE — lesen, bereinigen, schreiben
// ═══════════════════════════════════════════════════════════════════════════

interface ProjektEintrag {
    /** Roh, wie gespeichert — geprueft wird erst beim Lesen des Merkers. */
    station: string;
    am: string;
}

interface Ablage {
    ansicht?: PlanungAnsichtArt;
    hilfe: string[];
    /** Map statt Objekt: eine Projektkennung wie `__proto__` waere sonst ein
     *  Prototyp-Wechsel statt eines Eintrags. */
    projekte: Map<string, ProjektEintrag>;
}

const leer = (): Ablage => ({ hilfe: [], projekte: new Map() });

const istAnsicht = (v: unknown): v is PlanungAnsichtArt => v === 'leitfaden' || v === 'reiter';

const istObjekt = (v: unknown): v is Record<string, unknown> =>
    !!v && typeof v === 'object' && !Array.isArray(v);

/** Aus dem geparsten JSON nur das, was die erwartete Form hat. */
const bereinige = (roh: unknown): Ablage => {
    const ablage = leer();
    if (!istObjekt(roh)) return ablage;
    if (istAnsicht(roh.ansicht)) ablage.ansicht = roh.ansicht;
    if (Array.isArray(roh.hilfe)) {
        ablage.hilfe = [...new Set(roh.hilfe.filter((h): h is string => typeof h === 'string'))];
    }
    if (istObjekt(roh.projekte)) {
        Object.entries(roh.projekte).forEach(([id, wert]) => {
            if (!id || !istObjekt(wert)) return;
            ablage.projekte.set(id, {
                station: typeof wert.station === 'string' ? wert.station : '',
                am: typeof wert.am === 'string' ? wert.am : '',
            });
        });
    }
    return ablage;
};

/**
 * Liest die Ablage. `lesbar: false` heisst: der Speicher selbst hat
 * geworfen — dann wird auch nicht geschrieben, sonst ueberschriebe ein
 * voruebergehender Lesefehler die Merker aller anderen Projekte. Kaputtes
 * JSON dagegen gilt als leer und darf ersetzt werden.
 */
const lies = (speicher: SpeicherLike | null): { ablage: Ablage; lesbar: boolean } => {
    if (!speicher) return { ablage: leer(), lesbar: false };
    let roh: string | null;
    try {
        roh = speicher.getItem(LEITFADEN_SPEICHER_SCHLUESSEL);
    } catch {
        return { ablage: leer(), lesbar: false };
    }
    if (!roh) return { ablage: leer(), lesbar: true };
    try {
        return { ablage: bereinige(JSON.parse(roh)), lesbar: true };
    } catch {
        return { ablage: leer(), lesbar: true };
    }
};

/** Behaelt hoechstens `MAX_PROJEKTE`: das gerade geschriebene Projekt immer,
 *  dazu die juengsten nach `am`. ISO-Zeitpunkte sind lexikografisch
 *  vergleichbar; ein fehlendes `am` gilt als aeltestes. */
const kappe = (projekte: Map<string, ProjektEintrag>, behalte: string): Map<string, ProjektEintrag> => {
    if (projekte.size <= MAX_PROJEKTE) return projekte;
    const andere = [...projekte.entries()]
        .filter(([id]) => id !== behalte)
        .sort((a, b) => (a[1].am < b[1].am ? 1 : a[1].am > b[1].am ? -1 : 0))
        .slice(0, MAX_PROJEKTE - 1);
    const neu = new Map(andere);
    const eigener = projekte.get(behalte);
    if (eigener) neu.set(behalte, eigener);
    return neu;
};

const schreibe = (speicher: SpeicherLike | null, ablage: Ablage): void => {
    if (!speicher) return;
    const daten: Record<string, unknown> = {};
    if (ablage.ansicht) daten.ansicht = ablage.ansicht;
    if (ablage.hilfe.length) daten.hilfe = ablage.hilfe;
    // `Object.fromEntries` legt eigene Eigenschaften an — auch fuer `__proto__`.
    if (ablage.projekte.size) daten.projekte = Object.fromEntries(ablage.projekte);
    try {
        speicher.setItem(LEITFADEN_SPEICHER_SCHLUESSEL, JSON.stringify(daten));
    } catch {
        // Quota oder privates Fenster — der Merker ist Bequemlichkeit, nie ein Fehler.
    }
};

/** Lesen, aendern, schreiben — oder gar nichts, wenn der Speicher nicht lesbar ist. */
const aendere = (speicher: SpeicherLike | null, aenderung: (a: Ablage) => void): void => {
    const { ablage, lesbar } = lies(speicher);
    if (!lesbar) return;
    aenderung(ablage);
    schreibe(speicher, ablage);
};

// ═══════════════════════════════════════════════════════════════════════════
// ZULETZT BESUCHTE STATION (je Projekt)
// ═══════════════════════════════════════════════════════════════════════════

/** Die zuletzt besuchte Station des Projekts, `null` ohne Merker oder bei
 *  einem Stationsschluessel, den es nicht (mehr) gibt. */
export const ladeMerker = (
    projectId: string,
    speicher: SpeicherLike | null = browserSpeicher(),
): LeitfadenMerker | null => {
    if (!projectId) return null;
    const eintrag = lies(speicher).ablage.projekte.get(projectId);
    if (!eintrag || !istStationSchluessel(eintrag.station)) return null;
    return { station: eintrag.station, am: eintrag.am };
};

export const merkeStation = (
    projectId: string,
    station: StationSchluessel,
    amIso: string,
    speicher: SpeicherLike | null = browserSpeicher(),
): void => {
    if (!projectId || !istStationSchluessel(station)) return;
    aendere(speicher, a => {
        a.projekte.set(projectId, { station, am: String(amIso || '') });
        a.projekte = kappe(a.projekte, projectId);
    });
};

// ═══════════════════════════════════════════════════════════════════════════
// ANSICHT (je Browser, Entscheid E-L1)
// ═══════════════════════════════════════════════════════════════════════════

/** Gewaehlte Ansicht; Vorgabe fuer alle ist der Leitfaden. */
export const ladeAnsicht = (speicher: SpeicherLike | null = browserSpeicher()): PlanungAnsichtArt =>
    lies(speicher).ablage.ansicht ?? 'leitfaden';

export const merkeAnsicht = (
    ansicht: PlanungAnsichtArt,
    speicher: SpeicherLike | null = browserSpeicher(),
): void => {
    if (!istAnsicht(ansicht)) return;
    aendere(speicher, a => { a.ansicht = ansicht; });
};

// ═══════════════════════════════════════════════════════════════════════════
// HILFEKAESTEN („Verstanden", je Browser)
// ═══════════════════════════════════════════════════════════════════════════

export const hilfeAusgeblendet = (id: string, speicher: SpeicherLike | null = browserSpeicher()): boolean =>
    !!id && lies(speicher).ablage.hilfe.includes(id);

export const blendeHilfeAus = (id: string, speicher: SpeicherLike | null = browserSpeicher()): void => {
    if (!id) return;
    aendere(speicher, a => {
        if (!a.hilfe.includes(id)) a.hilfe.push(id);
    });
};
